// Server state with TanStack Query: one hook per resource of the dashboard API, and mutations that
// refresh what they change. Types come from @cck/schema, shared with cck-api.

import type {
  AlarmDetail, AlarmRule, AuthOptions, Billing, RedirectUrl, AlarmRuleInput, AlarmRuleVersion, AlarmSummary, Annotation, AnnotationInput, Asset, AssetDetail,
  AuditEntry, Calibration, Contact, ContactInput, CorrectiveAction, CorrectiveActionInput, Device, DeviceDetail,
  DoorSpan, EscalationPolicy, EscalationPolicyInput, ExportJob, ExportRequest, Invitation, Kiosk, KioskLink, KioskLinkCreated, Me, Member, MuteWindow,
  MuteWindowInput, NotificationPreferences, Organisation, OrganisationMembership, Overview, Page, Payment, Plan, Probe,
  LoraBaseDetail, LoraBaseSummary, ProbeAssignment, ProbeDetail, ProbeFeed, ProbeFeedCreated, SeriesResponse, Session, Site, Stats, Usage,
} from '@cck/schema';
import type {
  AssetFinancial, AssetFinancialInput, FleetEntry, InterventionAnnotationInput, InterventionInput, MaintenanceIntervention,
  MaintenanceMetrics, MaintenanceSchedule, MaintenanceSettings,
} from '@cck/schema';
import type {
  DirectMeterEnrollment, EnergyCandidate, EnergyCompare, EnergyFleet, EnergyMeter, EnergyPeriods, EnergySeries, EnergySettings,
  EnergySummary, Tariff, TariffDay,
} from '@cck/schema';
import type {
  CoolingCycle, CoolingCycleDetail, CoolingCycleInput, CoolingSettings, HaccpSettings,
} from '@cck/schema';
import type {
  CleaningCompletion, CleaningCompletionInput, CleaningServices, CleaningSiteDay, CleaningTask,
} from '@cck/schema';
import { type QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api.ts';

type List<T> = { data: T[] };
export type PaymentProviderName = 'stripe' | 'revolut';
type Json = Record<string, unknown>;

export const keys = {
  me: ['me'] as const,
  authOptions: ['auth-options'] as const,
  plans: ['plans'] as const,
  sessions: ['sessions'] as const,
  org: (o: string) => ['org', o] as const,
  scoped: (o: string, ...rest: unknown[]) => ['o', o, ...rest] as const,
};

/** The signed-in user; for a browser signed in with a kiosk link (no user), the kiosk, which reads as a viewer. */
export type DashboardUser = Me & { kiosk?: Kiosk };

const kioskUser = (k: Kiosk): DashboardUser => ({
  id: k.id, email: '', email_verified: true, display_name: k.name, locale: k.locale, timezone: 'Europe/Paris', phone: null,
  phone_verified: false, two_factor_enabled: true, channels: { email: false, sms: false, whatsapp: false, telegram: false },
  organisations: [{ org_id: k.org_id, name: k.org_name, role: 'viewer' }], impersonated_by: null, impersonation: null,
  kiosk: k,
});

/** The signed-in user (or kiosk), or null when signed out (401 is an answer here, not an error). */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async (): Promise<DashboardUser | null> => {
      try {
        return await api<Me>('/me');
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) throw err;
      }
      try {
        return kioskUser(await api<Kiosk>('/auth/kiosk'));
      } catch (err) {
        // A screen whose kiosk link was revoked says so (pages/Kiosk.tsx) rather than asking for a sign-in.
        if (err instanceof ApiError && err.code === 'kiosk_link_invalid') location.replace('/k');
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

/**
 * Whether anyone may sign up and create an organisation (GET /auth/options). Closed in production: the operating
 * team creates organisations and invites their Owner. Until it answers, the dashboard behaves as closed.
 */
export const useAuthOptions = () => useQuery({ queryKey: keys.authOptions, staleTime: Infinity, retry: 1,
  queryFn: () => api<AuthOptions>('/auth/options') });

/** True only once the API has said sign-up is open. */
export const useSelfSignUp = () => useAuthOptions().data?.self_sign_up === true;

export const usePlans = () => useQuery({ queryKey: keys.plans, queryFn: () => api<List<Plan>>('/plans'), staleTime: 3600_000 });
export const useSessions = () => useQuery({ queryKey: keys.sessions, queryFn: () => api<List<Session>>('/me/sessions') });

export const useOrg = (orgId: string) => useQuery({ queryKey: keys.org(orgId), queryFn: () => api<Organisation>(`/orgs/${orgId}`),
  retry: (n, err) => !(err instanceof ApiError && [401, 403, 404].includes(err.status)) && n < 2 });

function scoped<T>(orgId: string, path: string, key: unknown[], enabled = true) {
  return useQuery({ queryKey: keys.scoped(orgId, ...key), queryFn: () => api<T>(`/orgs/${orgId}${path}`), enabled });
}

export const useUsage = (o: string) => scoped<Usage>(o, '/usage', ['usage']);

/**
 * The subscription (Owners only). `until`: polled every 2 seconds until it holds, while a change made
 * with the payment provider (a payment, a change of plan) is being applied.
 */
export function useBilling(orgId: string, enabled = true, until: ((b: Billing) => boolean) | null = null) {
  return useQuery({ queryKey: keys.scoped(orgId, 'billing'), queryFn: () => api<Billing>(`/orgs/${orgId}/billing`), enabled,
    refetchInterval: (q) => (until && q.state.data && !until(q.state.data) ? 2000 : false) });
}
export const usePayments = (o: string, enabled = true) => scoped<List<Payment>>(o, '/billing/payments', ['payments'], enabled);
export const useMembers = (o: string) => scoped<List<Member>>(o, '/members', ['members']);
export const useInvitations = (o: string, enabled = true) => scoped<List<Invitation>>(o, '/invitations', ['invitations'], enabled);
export const useSites = (o: string, archived = false) =>
  scoped<List<Site>>(o, `/sites${archived ? '?include_archived=true' : ''}`, ['sites', archived]);
export const useAssets = (o: string) => scoped<List<Asset>>(o, '/assets', ['assets']);
export const useAsset = (o: string, id: string) => scoped<AssetDetail>(o, `/assets/${id}`, ['asset', id]);
export const useDevices = (o: string) => scoped<List<Device>>(o, '/devices', ['devices']);
export const useDevice = (o: string, id: string) => scoped<DeviceDetail>(o, `/devices/${id}`, ['device', id]);
export const useProbes = (o: string, filter = '') => scoped<List<Probe>>(o, `/probes${filter}`, ['probes', filter]);
export const useProbe = (o: string, id: string) => scoped<ProbeDetail>(o, `/probes/${id}`, ['probe', id]);
export const useCalibrations = (o: string, probeId: string) =>
  scoped<List<Calibration>>(o, `/probes/${probeId}/calibrations`, ['calibrations', probeId]);
export const useLoraBases = (o: string) => scoped<List<LoraBaseSummary>>(o, '/lora/bases', ['lora']);
export const useLoraBase = (o: string, id: string) => scoped<LoraBaseDetail>(o, `/lora/bases/${id}`, ['lora', id]);
export const useKioskLinks = (o: string) => scoped<List<KioskLink>>(o, '/kiosk-links', ['kiosk-links']);
export const useProbeFeeds = (o: string, probeId: string) =>
  scoped<List<ProbeFeed>>(o, `/probes/${probeId}/feeds`, ['feeds', probeId]);
export const useRules = (o: string, filter = '', enabled = true) =>
  scoped<List<AlarmRule>>(o, `/alarm-rules${filter}`, ['rules', filter], enabled);
export const useRuleVersions = (o: string, id: string) =>
  scoped<List<AlarmRuleVersion>>(o, `/alarm-rules/${id}/versions`, ['rule-versions', id]);
export const useMutes = (o: string) => scoped<List<MuteWindow>>(o, '/mute-windows', ['mutes']);
export const usePolicies = (o: string) => scoped<List<EscalationPolicy>>(o, '/escalation-policies', ['policies']);
export const useContacts = (o: string) => scoped<List<Contact>>(o, '/contacts', ['contacts']);
export const usePreferences = (o: string) => scoped<NotificationPreferences>(o, '/notification-preferences', ['prefs']);
export const useAlarm = (o: string, id: string) => scoped<AlarmDetail>(o, `/alarms/${id}`, ['alarm', id]);

// Maintenance (docs/architecture.md §20). A missing financial profile is an answer (null), not an error.
export const useMaintenanceSettings = (o: string) => scoped<MaintenanceSettings>(o, '/maintenance/settings', ['maintenance', 'settings']);
export function useAssetFinancial(orgId: string, assetId: string) {
  return useQuery({ queryKey: keys.scoped(orgId, 'maintenance', 'financial', assetId), queryFn: async () => {
    try {
      return await api<AssetFinancial>(`/orgs/${orgId}/assets/${assetId}/financial`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  } });
}
export const useInterventions = (o: string, assetId: string) =>
  scoped<List<MaintenanceIntervention>>(o, `/assets/${assetId}/maintenance`, ['maintenance', 'log', assetId]);
export const useSchedules = (o: string, assetId: string) =>
  scoped<List<MaintenanceSchedule>>(o, `/assets/${assetId}/maintenance-schedules`, ['maintenance', 'schedules', assetId]);
export const useMaintenanceMetrics = (o: string, assetId: string) =>
  scoped<MaintenanceMetrics>(o, `/assets/${assetId}/maintenance-metrics`, ['maintenance', 'metrics', assetId]);
export const useFleet = (o: string, query: string, enabled = true) =>
  scoped<List<FleetEntry>>(o, `/maintenance/fleet${query}`, ['maintenance', 'fleet', query], enabled);

// HACCP records (docs/architecture.md §22). The settings carry the modules in the plan (the navigation and tabs follow
// them); the module routes answer 402 otherwise. Running cooling cycles refresh every 30 seconds.
export const useHaccpSettings = (o: string) =>
  useQuery({ queryKey: keys.scoped(o, 'haccp', 'settings'), queryFn: () => api<HaccpSettings>(`/orgs/${o}/haccp/settings`),
    staleTime: 300_000 });
export const useCoolingSettings = (o: string, enabled = true) =>
  scoped<List<CoolingSettings>>(o, '/haccp/cooling/settings', ['haccp', 'cooling-settings'], enabled);
export const useCoolingCycles = (o: string, filter: string, enabled = true) =>
  useQuery({ queryKey: keys.scoped(o, 'haccp', 'cycles', filter), enabled, refetchInterval: 30_000,
    queryFn: () => api<Page<CoolingCycle>>(`/orgs/${o}/haccp/cooling/cycles?limit=50${filter}`) });
// The cleaning plan (HACCP module 3): today's checklist refreshes every minute.
export const useCleaningToday = (o: string, enabled = true) =>
  useQuery({ queryKey: keys.scoped(o, 'haccp', 'cleaning-today'), enabled, refetchInterval: 60_000,
    queryFn: () => api<List<CleaningSiteDay>>(`/orgs/${o}/haccp/cleaning/today`) });
export const useCleaningTasks = (o: string, enabled = true) =>
  scoped<List<CleaningTask>>(o, '/haccp/cleaning/tasks', ['haccp', 'cleaning-tasks'], enabled);
export const useCleaningServices = (o: string, enabled = true) =>
  scoped<List<CleaningServices>>(o, '/haccp/cleaning/services', ['haccp', 'cleaning-services'], enabled);
export const useCleaningCompletions = (o: string, filter: string, enabled = true) =>
  scoped<Page<CleaningCompletion>>(o, `/haccp/cleaning/completions?limit=200${filter}`, ['haccp', 'cleaning-completions', filter],
    enabled);
export const useCoolingCycle = (o: string, id: string) =>
  useQuery({ queryKey: keys.scoped(o, 'haccp', 'cycle', id), refetchInterval: (q) => (q.state.data?.status === 'running' ? 30_000 : false),
    queryFn: () => api<CoolingCycleDetail>(`/orgs/${o}/haccp/cooling/cycles/${id}`) });

// Energy (docs/architecture.md §21). Plan gating: the settings carry the features; the other routes answer 402.
export const useEnergySettings = (o: string) => scoped<EnergySettings>(o, '/energy/settings', ['energy', 'settings']);
export const useMeters = (o: string, filter = '', enabled = true) =>
  scoped<List<EnergyMeter>>(o, `/meters${filter}`, ['energy', 'meters', filter], enabled);
export const useEnergySummary = (o: string, assetId: string, enabled = true) =>
  scoped<EnergySummary>(o, `/assets/${assetId}/energy/summary`, ['energy', 'summary', assetId], enabled);
export function useEnergySeries(orgId: string, assetId: string, from: string, to: string, enabled = true) {
  return useQuery({ queryKey: keys.scoped(orgId, 'energy', 'series', assetId, from, to), enabled, placeholderData: (prev) => prev,
    queryFn: () => api<EnergySeries>(`/orgs/${orgId}/assets/${assetId}/energy/series?from=${encodeURIComponent(from)}`
      + `&to=${encodeURIComponent(to)}`) });
}
export const useEnergyPeriods = (o: string, assetId: string, period: string, from: string, to: string, enabled = true) =>
  scoped<EnergyPeriods>(o, `/assets/${assetId}/energy/periods?period=${period}&from=${from}&to=${to}`,
    ['energy', 'periods', assetId, period, from, to], enabled);
export const useEnergyCompare = (o: string, assetId: string, interventionId: string, days: number, enabled = true) =>
  scoped<EnergyCompare>(o, `/assets/${assetId}/energy/compare?intervention_id=${interventionId}&days=${days}`,
    ['energy', 'compare', assetId, interventionId, days], enabled && interventionId !== '');
export const useEnergyFleet = (o: string, query: string, enabled = true) =>
  scoped<EnergyFleet>(o, `/energy/fleet${query}`, ['energy', 'fleet', query], enabled);
export const useTariffs = (o: string, siteId: string, enabled = true) =>
  scoped<List<Tariff>>(o, `/sites/${siteId}/tariffs`, ['energy', 'tariffs', siteId], enabled);
export const useTariffDays = (o: string, siteId: string, from: string, to: string, enabled = true) =>
  scoped<List<TariffDay>>(o, `/sites/${siteId}/tariff-days?from=${from}&to=${to}`, ['energy', 'tariff-days', siteId, from, to], enabled);

/** Export jobs, polled while one is queued or running. */
export function useExports(orgId: string) {
  return useQuery({ queryKey: keys.scoped(orgId, 'exports'), queryFn: () => api<Page<ExportJob>>(`/orgs/${orgId}/exports?limit=50`),
    refetchInterval: (q) => (q.state.data?.data.some((j) => j.status === 'queued' || j.status === 'running') ? 3000 : false) });
}

export function useOverview(orgId: string) {
  return useQuery({ queryKey: keys.scoped(orgId, 'overview'), queryFn: () => api<Overview>(`/orgs/${orgId}/overview`),
    refetchInterval: 60_000 });
}

export function useAlarms(orgId: string, filter: string, limit = 50) {
  return useQuery({ queryKey: keys.scoped(orgId, 'alarms', filter, limit),
    queryFn: () => api<Page<AlarmSummary>>(`/orgs/${orgId}/alarms?limit=${limit}${filter ? `&${filter}` : ''}`),
    refetchInterval: 30_000 });
}

export function useSeries(orgId: string, probeIds: string[], from: string, to: string, tier = 'auto') {
  const ids = [...probeIds].sort().join(',');
  return useQuery({
    queryKey: keys.scoped(orgId, 'series', ids, from, to, tier),
    queryFn: () => api<SeriesResponse>(`/orgs/${orgId}/series?probe_ids=${ids}&from=${encodeURIComponent(from)}`
      + `&to=${encodeURIComponent(to)}&tier=${tier}`),
    enabled: probeIds.length > 0,
    placeholderData: (prev) => prev,
  });
}

export function useStats(orgId: string, probeId: string, from: string, to: string) {
  return useQuery({ queryKey: keys.scoped(orgId, 'stats', probeId, from, to),
    queryFn: () => api<Stats>(`/orgs/${orgId}/stats?probe_id=${probeId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`) });
}

export function useDoorSpans(orgId: string, assetId: string, from: string, to: string, enabled: boolean) {
  return useQuery({ queryKey: keys.scoped(orgId, 'doors', assetId, from, to), enabled,
    queryFn: () => api<List<DoorSpan>>(`/orgs/${orgId}/door-events?asset_id=${assetId}&from=${encodeURIComponent(from)}`
      + `&to=${encodeURIComponent(to)}`) });
}

export function useAnnotations(orgId: string, assetId: string, from: string, to: string) {
  return useQuery({ queryKey: keys.scoped(orgId, 'annotations', assetId, from, to),
    queryFn: () => api<List<Annotation>>(`/orgs/${orgId}/annotations?asset_id=${assetId}&from=${encodeURIComponent(from)}`
      + `&to=${encodeURIComponent(to)}`) });
}

/** A cursor-paginated list ("show more"). Refetches go through the pages in order, so they stay contiguous. */
function usePaged<T>(orgId: string, key: unknown[], path: string, refetchInterval: number | false = false) {
  return useInfiniteQuery({
    queryKey: keys.scoped(orgId, ...key),
    queryFn: ({ pageParam }) =>
      api<Page<T>>(`/orgs/${orgId}${path}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    refetchInterval,
  });
}

/** The alarm ledger, 50 at a time (the first pages refresh every 30 s). */
export const useAlarmPages = (orgId: string, filter: string) =>
  usePaged<AlarmSummary>(orgId, ['alarms', 'pages', filter], `/alarms?limit=50${filter ? `&${filter}` : ''}`, 30_000);

export const useAuditPages = (orgId: string, filter: string) =>
  usePaged<AuditEntry>(orgId, ['audit', filter], `/audit-log?limit=50${filter}`);

// ---------------------------------------------------------------- mutations

/** A mutation on the organisation's API that refreshes the listed query groups afterwards. */
export function useOrgMutation<I, O = unknown>(orgId: string, fn: (input: I) => Promise<O>, refresh: string[] = []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => refreshScoped(qc, orgId, refresh),
  });
}

export function refreshScoped(qc: QueryClient, orgId: string, groups: string[]): Promise<void> {
  return Promise.all(groups.map((g) => (g === 'org' ? qc.invalidateQueries({ queryKey: keys.org(orgId) })
    : qc.invalidateQueries({ queryKey: keys.scoped(orgId, g) })))).then(() => undefined);
}

export const orgApi = (orgId: string) => ({
  createSite: (b: Json) => api<Site>(`/orgs/${orgId}/sites`, { method: 'POST', body: b, idempotent: true }),
  updateSite: (id: string, b: Json) => api<Site>(`/orgs/${orgId}/sites/${id}`, { method: 'PATCH', body: b }),
  archiveSite: (id: string) => api(`/orgs/${orgId}/sites/${id}`, { method: 'DELETE' }),
  createAsset: (b: Json) => api<Asset>(`/orgs/${orgId}/assets`, { method: 'POST', body: b, idempotent: true }),
  updateAsset: (id: string, b: Json) => api<Asset>(`/orgs/${orgId}/assets/${id}`, { method: 'PATCH', body: b }),
  archiveAsset: (id: string) => api(`/orgs/${orgId}/assets/${id}`, { method: 'DELETE' }),
  claimDevice: (b: Json) => api<Device>(`/orgs/${orgId}/devices/claim`, { method: 'POST', body: b, idempotent: true }),
  updateDevice: (id: string, b: Json) => api<Device>(`/orgs/${orgId}/devices/${id}`, { method: 'PATCH', body: b }),
  revokeDevice: (id: string) => api(`/orgs/${orgId}/devices/${id}/revoke`, { method: 'POST' }),
  releaseDevice: (id: string) => api(`/orgs/${orgId}/devices/${id}/release`, { method: 'POST' }),
  updateProbe: (id: string, b: Json) => api<Probe>(`/orgs/${orgId}/probes/${id}`, { method: 'PATCH', body: b }),
  assignProbe: (id: string, assetId: string) =>
    api<ProbeAssignment>(`/orgs/${orgId}/probes/${id}/assignment`, { method: 'PUT', body: { asset_id: assetId } }),
  unassignProbe: (id: string) => api(`/orgs/${orgId}/probes/${id}/assignment`, { method: 'DELETE' }),
  addCalibration: (probeId: string, b: Json) =>
    api<Calibration>(`/orgs/${orgId}/probes/${probeId}/calibrations`, { method: 'POST', body: b }),
  createProbeFeed: (probeId: string, b: Json) =>
    api<ProbeFeedCreated>(`/orgs/${orgId}/probes/${probeId}/feeds`, { method: 'POST', body: b }),
  createKioskLink: (b: { name: string; site_id?: string }) =>
    api<KioskLinkCreated>(`/orgs/${orgId}/kiosk-links`, { method: 'POST', body: b }),
  revokeKioskLink: (linkId: string) => api(`/orgs/${orgId}/kiosk-links/${linkId}`, { method: 'DELETE' }),
  revokeProbeFeed: (probeId: string, feedId: string) =>
    api(`/orgs/${orgId}/probes/${probeId}/feeds/${feedId}`, { method: 'DELETE' }),
  createRule: (b: AlarmRuleInput) => api<AlarmRule>(`/orgs/${orgId}/alarm-rules`, { method: 'POST', body: b, idempotent: true }),
  replaceRule: (id: string, version: number, b: AlarmRuleInput) =>
    api<AlarmRule>(`/orgs/${orgId}/alarm-rules/${id}`, { method: 'PUT', body: b, headers: { 'if-match': `"${version}"` } }),
  archiveRule: (id: string) => api(`/orgs/${orgId}/alarm-rules/${id}`, { method: 'DELETE' }),
  createMute: (b: MuteWindowInput) => api<MuteWindow>(`/orgs/${orgId}/mute-windows`, { method: 'POST', body: b }),
  cancelMute: (id: string) => api(`/orgs/${orgId}/mute-windows/${id}`, { method: 'DELETE' }),
  createPolicy: (b: EscalationPolicyInput) =>
    api<EscalationPolicy>(`/orgs/${orgId}/escalation-policies`, { method: 'POST', body: b }),
  replacePolicy: (id: string, etag: string, b: EscalationPolicyInput) =>
    api<EscalationPolicy>(`/orgs/${orgId}/escalation-policies/${id}`, { method: 'PUT', body: b, headers: { 'if-match': etag } }),
  deletePolicy: (id: string) => api(`/orgs/${orgId}/escalation-policies/${id}`, { method: 'DELETE' }),
  createContact: (b: ContactInput) => api<Contact>(`/orgs/${orgId}/contacts`, { method: 'POST', body: b }),
  updateContact: (id: string, b: ContactInput) => api<Contact>(`/orgs/${orgId}/contacts/${id}`, { method: 'PATCH', body: b }),
  archiveContact: (id: string) => api(`/orgs/${orgId}/contacts/${id}`, { method: 'DELETE' }),
  setPreferences: (b: NotificationPreferences) =>
    api<NotificationPreferences>(`/orgs/${orgId}/notification-preferences`, { method: 'PUT', body: b }),
  acknowledge: (alarmId: string, b: CorrectiveActionInput) =>
    api<AlarmDetail>(`/orgs/${orgId}/alarms/${alarmId}/acknowledge`, { method: 'POST', body: b, idempotent: true }),
  addCorrectiveAction: (alarmId: string, b: CorrectiveActionInput) =>
    api<CorrectiveAction>(`/orgs/${orgId}/alarms/${alarmId}/corrective-actions`, { method: 'POST', body: b }),
  annotate: (b: AnnotationInput) => api<Annotation>(`/orgs/${orgId}/annotations`, { method: 'POST', body: b }),
  createExport: (b: ExportRequest) => api<ExportJob>(`/orgs/${orgId}/exports`, { method: 'POST', body: b, idempotent: true }),
  updateOrg: (b: Json) => api<Organisation>(`/orgs/${orgId}`, { method: 'PATCH', body: b }),
  requestDeletion: (name: string) =>
    api<{ erasure_after: string }>(`/orgs/${orgId}/deletion`, { method: 'PUT', body: { confirm_name: name } }),
  cancelDeletion: () => api(`/orgs/${orgId}/deletion`, { method: 'DELETE' }),
  checkout: (priceId: string, provider: PaymentProviderName) => api<RedirectUrl>(`/orgs/${orgId}/billing/checkout`,
    { method: 'POST', body: { price_id: priceId, provider }, idempotent: true }),
  billingPortal: () => api<RedirectUrl>(`/orgs/${orgId}/billing/portal`, { method: 'POST' }),
  changePlan: (priceId: string) => api<{ plan_id: string; effective_at: string | null }>(`/orgs/${orgId}/billing/change-plan`,
    { method: 'POST', body: { price_id: priceId }, idempotent: true }),
  cancelSubscription: () => api<{ cancel_at: string | null }>(`/orgs/${orgId}/billing/cancel`, { method: 'POST' }),
  resumeSubscription: () => api<{ cancel_at: null }>(`/orgs/${orgId}/billing/resume`, { method: 'POST' }),
  payOverdue: () => api<RedirectUrl>(`/orgs/${orgId}/billing/pay`, { method: 'POST' }),
  invite: (b: Json) => api<Invitation>(`/orgs/${orgId}/invitations`, { method: 'POST', body: b, idempotent: true }),
  revokeInvitation: (id: string) => api(`/orgs/${orgId}/invitations/${id}`, { method: 'DELETE' }),
  changeRole: (userId: string, role: string) =>
    api<Member>(`/orgs/${orgId}/members/${userId}`, { method: 'PATCH', body: { role } }),
  removeMember: (userId: string) => api(`/orgs/${orgId}/members/${userId}`, { method: 'DELETE' }),
  updateMaintenanceSettings: (b: Json) => api<MaintenanceSettings>(`/orgs/${orgId}/maintenance/settings`, { method: 'PUT', body: b }),
  putFinancial: (assetId: string, b: AssetFinancialInput) =>
    api<AssetFinancial>(`/orgs/${orgId}/assets/${assetId}/financial`, { method: 'PUT', body: b }),
  logIntervention: (assetId: string, b: InterventionInput) =>
    api<MaintenanceIntervention>(`/orgs/${orgId}/assets/${assetId}/maintenance`, { method: 'POST', body: b, idempotent: true }),
  annotateIntervention: (id: string, b: InterventionAnnotationInput) =>
    api<MaintenanceIntervention>(`/orgs/${orgId}/maintenance/interventions/${id}/annotations`, { method: 'POST', body: b }),
  createSchedule: (assetId: string, b: Json) =>
    api<MaintenanceSchedule>(`/orgs/${orgId}/assets/${assetId}/maintenance-schedules`, { method: 'POST', body: b, idempotent: true }),
  updateSchedule: (id: string, b: Json) => api<MaintenanceSchedule>(`/orgs/${orgId}/maintenance/schedules/${id}`, { method: 'PATCH', body: b }),
  archiveSchedule: (id: string) => api(`/orgs/${orgId}/maintenance/schedules/${id}`, { method: 'DELETE' }),
  updateEnergySettings: (b: Json) => api<EnergySettings>(`/orgs/${orgId}/energy/settings`, { method: 'PUT', body: b }),
  updateMeter: (probeId: string, b: Json) => api<EnergyMeter>(`/orgs/${orgId}/meters/${probeId}`, { method: 'PATCH', body: b }),
  enrollDirectMeter: (b: Json) =>
    api<DirectMeterEnrollment>(`/orgs/${orgId}/meters/direct`, { method: 'POST', body: b, idempotent: true }),
  rotateDirectMeter: (deviceId: string) =>
    api<{ websocket_url: string }>(`/orgs/${orgId}/meters/direct/${deviceId}/address`, { method: 'POST' }),
  createTariff: (siteId: string, b: Json) =>
    api<Tariff>(`/orgs/${orgId}/sites/${siteId}/tariffs`, { method: 'POST', body: b, idempotent: true }),
  replaceTariff: (siteId: string, id: string, b: Json) =>
    api<Tariff>(`/orgs/${orgId}/sites/${siteId}/tariffs/${id}`, { method: 'PUT', body: b }),
  deleteTariff: (siteId: string, id: string) => api(`/orgs/${orgId}/sites/${siteId}/tariffs/${id}`, { method: 'DELETE' }),
  putTariffDays: (siteId: string, days: { day: string; day_type: string | null }[]) =>
    api(`/orgs/${orgId}/sites/${siteId}/tariff-days`, { method: 'PUT', body: { days } }),
  putCandidate: (assetId: string, b: Json) =>
    api<EnergyCandidate>(`/orgs/${orgId}/assets/${assetId}/energy/candidate`, { method: 'PUT', body: b }),
  deleteCandidate: (assetId: string) => api(`/orgs/${orgId}/assets/${assetId}/energy/candidate`, { method: 'DELETE' }),
  resetBaseline: (assetId: string) => api(`/orgs/${orgId}/assets/${assetId}/energy/baseline/reset`, { method: 'POST' }),
  updateHaccpSettings: (b: Json) => api<HaccpSettings>(`/orgs/${orgId}/haccp/settings`, { method: 'PUT', body: b }),
  putCoolingSettings: (siteId: string, b: Json) =>
    api<CoolingSettings>(`/orgs/${orgId}/haccp/cooling/settings/${siteId}`, { method: 'PUT', body: b }),
  // Offline-ready: the client's ID makes a retry safe (the same ID and content return the same cycle).
  startCooling: (b: CoolingCycleInput) =>
    api<CoolingCycleDetail>(`/orgs/${orgId}/haccp/cooling/cycles`, { method: 'POST', body: b }),
  closeCooling: (id: string, b: Json) =>
    api<CoolingCycleDetail>(`/orgs/${orgId}/haccp/cooling/cycles/${id}/close`, { method: 'POST', body: b }),
  cancelCooling: (id: string, b: Json) =>
    api<CoolingCycleDetail>(`/orgs/${orgId}/haccp/cooling/cycles/${id}/cancel`, { method: 'POST', body: b }),
  correctCooling: (id: string, b: Json) =>
    api<CoolingCycleDetail>(`/orgs/${orgId}/haccp/cooling/cycles/${id}/corrections`, { method: 'POST', body: b }),
  putCleaningServices: (siteId: string, b: Json) =>
    api<CleaningServices>(`/orgs/${orgId}/haccp/cleaning/services/${siteId}`, { method: 'PUT', body: b }),
  createCleaningTask: (b: Json) => api<CleaningTask>(`/orgs/${orgId}/haccp/cleaning/tasks`, { method: 'POST', body: b }),
  updateCleaningTask: (id: string, b: Json) =>
    api<CleaningTask>(`/orgs/${orgId}/haccp/cleaning/tasks/${id}`, { method: 'PUT', body: b }),
  archiveCleaningTask: (id: string) => api<CleaningTask>(`/orgs/${orgId}/haccp/cleaning/tasks/${id}/archive`, { method: 'POST' }),
  // Offline-ready, as startCooling.
  completeCleaning: (b: CleaningCompletionInput) =>
    api<CleaningCompletion>(`/orgs/${orgId}/haccp/cleaning/completions`, { method: 'POST', body: b }),
  correctCleaning: (id: string, b: Json) =>
    api<CleaningCompletion>(`/orgs/${orgId}/haccp/cleaning/completions/${id}/corrections`, { method: 'POST', body: b }),
});

export type { OrganisationMembership };
